<template>
  <div class="incident-list-item">
    <v-row>
      <v-col cols="3" class="d-flex flex-column justify-center align-center align text-center">
        <div class="logo">
          <v-avatar :color="props.item.sfdiColor ?? ''">
            <v-icon :size="50" color="rgb(var(--v-theme-on-background))">mdi-fire</v-icon>
          </v-avatar>
          <p class="font-italic small-text">
            {{ props.item.timeAgo ?? getDateAgo }}
          </p>
        </div>
      </v-col>

      <v-col cols="9">
        <v-row>
          <v-col cols="12">
            <p>{{ props.item.name }}</p>
            <span class="text-body-2 font-weight-light" :title="firstLineInfo">
              {{ firstLineInfo }}
            </span>
            <p class="text-body-2 font-weight-light" :title="secondLineInfo">
              {{ secondLineInfo }}
            </p>
          </v-col>
        </v-row>
        <v-col v-if="props.item.extraValues?.hasFobs">
          <v-icon icon="mdi-form-select" color="primary" size="x-small" class="mr-1" />
          <v-icon v-if="props.item.extraValues?.hasPopulation" icon="mdi-shield-home" color="primary" size="x-small" class="mr-1" />
        </v-col>
        <!--v-col cols="12" class="d-flex justify-end pr-2 pb-2 pt-1 pl-0">
          <v-btn variant="plain" density="compact" :ripple="false" icon @click.stop="goToExtent()">
            <v-icon size="25">mdi-map-marker</v-icon>
            <v-tooltip activator="parent" location="bottom">{{ $t("common.actions.locate") }}</v-tooltip>
          </v-btn>
          <v-btn variant="plain" density="compact" :ripple="false" icon @click.stop="copyIncident()">
            <v-icon size="25">mdi-link-variant</v-icon>
            <v-tooltip activator="parent" location="bottom">{{ $t("common.concepts.link") }}</v-tooltip>
          </v-btn>
          <v-btn variant="plain" density="compact" :ripple="false" icon @click.stop="pinOrUnpin()">
            <v-icon size="25">{{ isPinned ? "mdi-pin-off" : "mdi-pin" }}</v-icon>
            <v-tooltip activator="parent" location="bottom">
              {{ isPinned ? $t("common.actions.unpin") : $t("common.actions.pin") }}
            </v-tooltip>
          </v-btn>
        </v-col-->
      </v-col>
    </v-row>

    <v-row class="item_action_buttons">
      <v-col cols="12" class="d-flex justify-end pr-2 pb-3 pt-0 pl-0">
        <v-btn class="action_button" variant="plain" :ripple="false" icon @click.stop="goToExtent()">
          <v-icon size="18">mdi-map-marker</v-icon>
          <v-tooltip activator="parent" location="bottom">{{ $t('common.actions.locate') }}</v-tooltip>
        </v-btn>
        <v-btn class="action_button" variant="plain" :ripple="false" icon @click.stop="copyIncident()">
          <v-icon size="18">mdi-link-variant</v-icon>
          <v-tooltip activator="parent" location="bottom">{{ $t('common.concepts.link') }}</v-tooltip>
        </v-btn>
        <v-btn class="action_button" variant="plain" :ripple="false" icon @click.stop="pinOrUnpin()">
          <v-icon size="18">{{ isPinned ? 'mdi-pin-off' : 'mdi-pin' }}</v-icon>
          <v-tooltip activator="parent" location="bottom">
            {{ isPinned ? $t('common.actions.unpin') : $t('common.actions.pin') }}
          </v-tooltip>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
// Store import
import SettingsStore from '@/stores/settingsStore.ts'
const settings = SettingsStore()

// imports Framework
import type { PropType } from 'vue'
import { computed } from 'vue'

import { storeToRefs } from 'pinia'

import { useClipboard } from '@vueuse/core'
import { formatDistance } from 'date-fns'
import { es, enUS, ptBR, fr } from 'date-fns/locale'
import { useI18n } from 'vue-i18n'

// import Type

//import Store

// Zapan Imports

// Karonte imports
import useView from '@tsyl/karonte/lib/composables/useView'
import type { Coordinates } from '@tsyl/karonte/lib/types/coordinates'
import { TimezonedDateTime, isNullOrUndefined, utcVal } from '@tsyl/radon'
import { useNotifications } from '@tsyl/zapan2'

import { incidentsPinnedStore } from '@/stores/pinnedIncidents'
import type { IncidentListItem } from '@/types/IncidentListItem'

const { getReactiveView } = useView()
const timezone = import.meta.env.VITE_TIMEZONE

const { addNotification } = useNotifications()

const props = defineProps({
  item: {
    type: Object as PropType<IncidentListItem>,
    required: true
  }
})

const { locale, t } = useI18n()

const discoveryDate = computed(() => {
  if (props.item.discoveryTimestamp) {
    const utcValue = utcVal(props.item.discoveryTimestamp)
    const timeObj = new TimezonedDateTime(timezone)
    timeObj.setUTC(utcValue)
    return timeObj
  } else {
    return null
  }
})

function getLocaleForLanguage(language: string) {
  switch (language) {
    case 'pt-BR':
      return ptBR
    case 'en-US':
      return enUS
    case 'es-ES':
      return es
    case 'fr-FR':
      return fr
    default:
      return enUS
  }
}

const getDateAgo = computed(() => {
  if (discoveryDate.value) {
    const discoveryDateStr = discoveryDate.value?.toString() as string
    const currentLocale = getLocaleForLanguage(locale.value)
    const formatDist = formatDistance(new Date(discoveryDateStr), new Date(), {
      locale: currentLocale
    })
    return formatDist.charAt(0).toUpperCase() + formatDist.slice(1)
  } else {
    return ''
  }
})

// TODO: review backend data to replace content
const firstLineInfo = computed(() => {
  const values: string[] = []
  if (!isNullOrUndefined(props.item.firstLine)) return props.item.firstLine
  if (!isNullOrUndefined(props.item.fireIdentifier)) values.push(props.item.fireIdentifier as string)
  if (!isNullOrUndefined(props.item.adminDivision)) values.push(props.item.adminDivision as string)
  return values.join(' · ')
})

const secondLineInfo = computed(() => {
  const values: string[] = []
  if (
    !isNullOrUndefined(props.item.secondLine) &&
    !isNullOrUndefined(props.item.totalAcres) &&
    !isNullOrUndefined(props.item.percentContained)
  )
    return props.item.secondLine

  values.push(`${props.item.totalAcres ? props.item.totalAcres : '-'} ha`)
  values.push(`${props.item.percentContained ? props.item.percentContained : '-'} %`)
  values.push(`${settings.formatStringDateTime(props.item.discoveryTimestamp ?? '') ?? '-'}`)

  return values.join(' · ')
})

const { pinned } = storeToRefs(incidentsPinnedStore())

const view = getReactiveView()

const isPinned = computed(() => {
  return pinned.value.some((p) => p.id === props.item.id)
})

function goToExtent() {
  const coordinates: Coordinates = {
    latitude: props.item.extraValues?.latitude as number,
    longitude: props.item.extraValues?.longitude as number
  }

  view.value?.goTo(coordinates, 17)
}

function copyIncident() {
  addNotification({
    type: 'success',
    title: t('domain.fields.coordinates').concat(' ', t('messages.success.copied')),
    time: 2000
  })

  const { copy } = useClipboard({
    source: `${window.location.origin}/?incident=${props.item.id}`
  })
  return copy()
}

function pinOrUnpin() {
  if (isPinned.value) {
    unPinIncident()
  } else {
    pinIncident()
  }
}

function pinIncident() {
  pinned.value.push(props.item)
}

function unPinIncident() {
  pinned.value = pinned.value.filter((i) => {
    return i.id !== props.item.id
  })
}
</script>

<style scoped lang="scss">
:deep(.logo i) {
  font-size: 36px !important;
}

:deep(.logo p) {
  padding-top: 4px !important;
  font-size: 12px !important;
}

:deep(.flex-row-reverse) {
  padding: 4px !important;
}

.item_action_buttons {
  margin-top: 0 !important;
  position: relative !important;
  bottom: 0;
  right: 0;
}

.action_button {
  margin: 0 !important;
  padding: 0 !important;
  width: 20px !important;
  height: 20px !important;
}
</style>
